import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { SystemActions } from '../actions';
import { CustomizationService } from '../services/customization.service';

@Injectable()
export class SystemEffects {

  loadManagedCustomization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SystemActions.loadManagedCustomization),
      switchMap(() => {
        return this.customizationService.listManagedCustomizations().pipe(
          mergeMap((response: any) => {
            if (response?.customizations?.length > 0) {
              return this.customizationService.getManagedCustomization(response.customizations[0].id).pipe(
                mergeMap((res: any) => {
                  // Flag the borrowed background/logo isSystemDefault, the same way
                  // team.effects.ts's own managed-customization fallback does: forwarding the
                  // template asset with no marker at all let an id-less placeholder reach
                  // downstream code that relies on this flag to recognise it, crashing on a
                  // missing `.id`. Unlike the earlier fix, this does NOT overwrite `type` --
                  // doing so destroyed the real media kind ('Image' / 'Youtube' / 'Vimeo') that
                  // smash-previewer-customization.component.html needs to render the asset at
                  // all, which is what made a never-customized team's default background/logo
                  // silently stop rendering everywhere this effect's output was displayed.
                  const customization = {
                    ...res.customization,
                    ...(res.customization.background ? { background: { ...res.customization.background, isSystemDefault: true } } : {}),
                    ...(res.customization.logo ? { logo: { ...res.customization.logo, isSystemDefault: true } } : {}),
                  };
                  return of(SystemActions.loadManagedCustomizationSuccess({ customization }));
                }),
                catchError((error: HttpErrorResponse) =>
                  of(SystemActions.loadManagedCustomizationFailure({ error: error.message }))
                )
              );
            } else {
              return of(SystemActions.loadManagedCustomizationFailure({ error: 'No Managed Customization' }))
            }

          }), catchError((error: HttpErrorResponse) =>
            of(SystemActions.loadManagedCustomizationFailure({ error: error.message }))
          ));
      }))
  );

  loadManagedBackgrounds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SystemActions.loadManagedBackgrounds),
      switchMap(() => {
        return this.customizationService.listManagedBackgrounds().pipe(
          map((response: any) => {
            return SystemActions.loadManagedBackgroundsSuccess({ backgrounds: response.backgrounds });
          }),
          catchError((error: HttpErrorResponse) =>
            of(SystemActions.loadManagedBackgroundsFailure({ error: error.message }))
          )
        );
      }))
  );

  loadManagedLogos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SystemActions.loadManagedLogos),
      switchMap(() => {
        return this.customizationService.listManagedLogos().pipe(
          map((response: any) => {
            return SystemActions.loadManagedLogosSuccess({ logos: response.logos });
          }),
          catchError((error: HttpErrorResponse) =>
            of(SystemActions.loadManagedLogosFailure({ error: error.message }))
          )
        );
      }))
  );

  constructor(
    private actions$: Actions,
    private readonly customizationService: CustomizationService,
  ) { }

}
